/**
 * Verify Email Page
 * Handles email verification token from URL
 */

import { useState, useEffect } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { api } from '@/lib/api';

export function VerifyEmailPage() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('No verification token provided');
      return;
    }

    api(`/auth/verify?token=${encodeURIComponent(token)}`)
      .then(() => setStatus('success'))
      .catch((err) => {
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Verification failed');
      });
  }, [token]);

  return (
    <div className="flex min-h-[80vh] items-center justify-center">
      <div className="w-full max-w-sm space-y-6 text-center">
        {status === 'loading' && (
          <div className="text-muted-foreground">Verifying your email...</div>
        )}

        {status === 'success' && (
          <div className="space-y-4">
            <div className="rounded-md bg-green-500/10 px-4 py-6 text-green-500">
              <h2 className="text-lg font-bold">Email verified</h2>
              <p className="mt-2 text-sm">Your account is now active.</p>
            </div>
            <Link to="/login" className="text-sm text-primary hover:underline">
              Sign in
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-4">
            <div className="rounded-md bg-red-500/10 px-4 py-6 text-red-500">
              <h2 className="text-lg font-bold">Verification failed</h2>
              <p className="mt-2 text-sm">{error}</p>
            </div>
            <Link to="/login" className="text-sm text-primary hover:underline">
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
